import React, { useEffect, useState } from "react";

type StartProjectModalProps = {
  triggerId: string;
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
};

export default function StartProjectModal({
  triggerId,
  title,
  body,
  ctaLabel,
  ctaHref,
}: StartProjectModalProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Function to set up event listener when trigger is found
    const setupTrigger = () => {
      const trigger = document.getElementById(triggerId);
      if (!trigger) {
        return null;
      }

      const handleClick = (event: Event) => {
        event.preventDefault();
        setOpen(true);
      };

      trigger.addEventListener("click", handleClick);
      return () => trigger.removeEventListener("click", handleClick);
    };

    // Try to set up immediately
    let cleanup = setupTrigger();

    // If trigger not found, watch for it to be added
    if (!cleanup) {
      const observer = new MutationObserver(() => {
        cleanup = setupTrigger();
        if (cleanup) {
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => {
        observer.disconnect();
        cleanup?.();
      };
    }

    return cleanup;
  }, [triggerId]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white max-w-lg w-full mx-6 p-8 rounded-lg shadow-xl">
        <h3 className="text-2xl sora-bold text-cila-slate-900">{title}</h3>
        <p className="text-slate-600 mt-3">{body}</p>
        <div className="flex gap-3 mt-6">
          <a
            className="px-6 py-3 text-white bg-cil-red hover:bg-cil-red/80 transition"
            href={ctaHref}
          >
            {ctaLabel}
          </a>
          <button
            className="px-6 py-3 border border-slate-200 hover:bg-slate-100 transition"
            type="button"
            onClick={() => setOpen(false)}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
